import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import { DownloadButton } from '@components/DownloadButton';
import { DrilldownHeader } from '@components/DrilldownHeader';
import GroupName from '@components/GroupName/GroupName';
import Icon from '@components/Icon/Icon';

import { isDevelopment } from '@constants/env';
import * as ROUTES from '@constants/routes';

import { useSendGroupInvite } from '@hooks/api/group';
import { useListUserImportJobs } from '@hooks/api/user';
import { useSnackbar } from '@hooks/useSnackbar';

import AppLayout from '@layouts/AppLayout';
import { MainContainer } from '@layouts/DrilldownStyle';

import { formatDate } from '@utils/format';

import type { GroupModel } from 'generated/types';

import PaginationFooter from './PaginationFooter';

const messages = defineMessages({
  title: {
    id: 'importJobList.title',
    defaultMessage: 'User Import Job List',
  },
  noJobs: {
    id: 'importJobList.noJobs',
    defaultMessage: 'No Import Jobs',
  },
  inviteError: {
    id: 'importJobList.inviteError',
    defaultMessage:
      'An error occurred while attempting to send an email invitation to all users in the group, {name}.',
  },
  inviteSuccess: {
    id: 'importJobList.inviteSuccess',
    defaultMessage:
      'An email invitation was sent to all users in the group, {name}.',
  },
});

const LIMIT = 10;

const UserJobList: React.FC = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: jobsData,
    loading,
    refetch,
  } = useListUserImportJobs({
    limit: LIMIT,
  });
  const [sendInvite] = useSendGroupInvite();

  const [page, setPage] = useState<number>(0);
  // Each element in tokens corresponds to the pagination token for a page number
  const [tokens, setTokens] = useState<(string | null)[]>([]);

  const handlePageChange = async (newPage: number) => {
    // No more jobs to fetch
    if (newPage > page && page > 0 && !tokens[page]) {
      return;
    }
    // If new page is out of range, use most recent token to fetch new batch
    const token = newPage === 0 ? null : (tokens.at(newPage - 1) ?? null);
    await refetch({
      input: {
        limit: LIMIT,
        token,
      },
    });
    setPage(newPage);
  };

  const sendEmailInvite = async (group: GroupModel) => {
    const { group_id, group_name } = group;
    try {
      const emailSent = await sendInvite({
        input: {
          group_id,
        },
      });
      if (emailSent.data?.sendGroupEmailInvite) {
        enqueueSnackbar({
          message: formatMessage(messages.inviteSuccess, {
            name: group_name,
          }),
          variant: 'success',
        });
      } else {
        enqueueSnackbar({
          message: formatMessage(messages.inviteError, {
            name: group_name,
          }),
          variant: 'error',
        });
      }
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const handleSubmit = async () => {
    await refetch({
      input: {
        limit: LIMIT,
        token: page === 0 ? null : tokens[page],
      },
    });
  };

  const tableData = useMemo(() => {
    return (jobsData?.UserImportJobs ?? []).map(
      ({
        AccountsInGroup,
        CompletionDate,
        FailedUsers,
        JobDetails,
        JobName,
        ImportedUsers,
        SkippedUsers,
      }) => {
        return {
          completionDate: CompletionDate,
          failedNum: FailedUsers,
          skippedNum: SkippedUsers,
          importedNum: ImportedUsers,
          accountsNum: AccountsInGroup,
          jobName: JobName,
          document_id: JobDetails?.document.document_id,
          document_name: JobDetails?.document.name,
          invite: JobDetails?.group,
          group: JobDetails?.group,
        };
      }
    );
  }, [jobsData?.UserImportJobs]);

  useEffect(() => {
    const tokensCopy = [...tokens];
    if (!tokensCopy[page]) {
      tokensCopy[page] = jobsData?.PaginationToken ?? null;
      setTokens(tokensCopy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsData?.PaginationToken]);

  return (
    <AppLayout showHeader>
      <DrilldownHeader
        backRoute={ROUTES.ADMIN_NEW_ACCOUNT}
        title={formatMessage(messages.title)}
      />
      <MainContainer>
        <ContentLoader loading={loading}>
          <MuiDataTable
            columns={[
              {
                name: 'document_name',
                label: 'File Name',
              },
              // {
              //   name: 'jobName',
              //   label: 'Job Name',
              //   options: {
              //     searchable: true,
              //   },
              // },
              {
                name: 'completionDate',
                className: 'date',
                label: 'Completion Date',
                options: {
                  customBodyRender: (date: string) =>
                    formatDate({ date, formatter: 'datetime' }),
                  filter: true,
                  sort: true,
                },
              },
              {
                name: 'importedNum',
                label: '# of Users',
                options: {
                  customBodyRender: (num?: number) => num ?? 0,
                  sort: true,
                },
              },
              {
                name: 'skippedNum',
                label: '# of Skipped',
                options: {
                  customBodyRender: (num?: number) => num ?? 0,
                  sort: true,
                },
              },
              {
                name: 'failedNum',
                label: '# of Failed',
                options: {
                  customBodyRender: (num?: number) => num ?? 0,
                  sort: true,
                },
              },
              {
                name: 'accountsNum',
                label: '# of Accounts',
                options: {
                  customBodyRender: (num?: number) => num ?? 0,
                  sort: true,
                },
              },
              {
                name: 'group',
                label: 'Group Name',
                options: {
                  filter: true,
                  searchable: true,
                  customBodyRender: (group?: GroupModel) =>
                    group ? (
                      <GroupName onSubmit={handleSubmit} value={group} />
                    ) : null,
                },
              },
              {
                name: 'document_id',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (document_id?: string) =>
                    document_id ? (
                      <DownloadButton
                        document_id={document_id}
                        type="download"
                      />
                    ) : (
                      ''
                    ),
                },
              },
              {
                name: 'invite',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (group?: GroupModel) =>
                    group ? (
                      <Icon
                        clickable
                        color="text.secondary"
                        name="envelope"
                        onClick={() => sendEmailInvite(group)}
                        size="lg"
                        type="fas"
                      />
                    ) : null,
                },
              },
            ]}
            data={tableData}
            options={{
              search: true,
              sort: true,
              filter: true,
              sortOrder: {
                direction: 'desc',
                name: 'completionDate',
              },
              textLabels: {
                body: {
                  noMatch: formatMessage(messages.noJobs),
                },
              },
              page: page,
              pagination: true,
              customFooter: () =>
                (jobsData?.UserImportJobs?.length ?? 0) > 0 ? (
                  <PaginationFooter changePage={handlePageChange} page={page} />
                ) : null,
              rowsPerPage: LIMIT,
            }}
          />
        </ContentLoader>
      </MainContainer>
    </AppLayout>
  );
};

export default UserJobList;
